import * as React from 'react';

// @ts-expect-error
import type {FieldProps} from 'redux-form';
import _ from 'underscore';

import {EmailEditor, PLACEHOLDER_ACTION_REPLACE} from 'shells/email-editor/email-editor';

import {
    doesKeyCancelTranscribe,
    mutateQuillHtmlValueForTranscribing,
} from '../../../speech-to-text';
import {TranscribeButtonContainer} from '../../../speech-to-text/transcribe-button-container';

import {InsertQuoteLinkButton} from '../insert-quote-link-button';

type QuillOnChange = (htmlValue: string, textValue: string) => void;

// This is a bit confusing, but the idea is that we need to be able to use
// the onChange from the field's input, but also get the plain text from the
// react-quill editor to change the plaintextBody.  So the `onBodyChange` that
// this component expects is a function creator.
type OnBodyChange = (input: {onChange: (...args: any) => any}) => QuillOnChange;

type OwnProps = {
    isActive: boolean;
    isFocused: boolean;
    onBodyChange: OnBodyChange;
    isMultiSequence: boolean;
    fileAttachmentFieldName: string;
    sendAsReply: boolean;
    notificationBanner?: React.ComponentType<any>;
    isLoading: boolean;
    isBodyFocused?: boolean;
    suggestedReplyToInsert?: string | null | undefined;
};

type Props = FieldProps & OwnProps;

type State = {
    value: string;
    valueSnapshot: string;
    latestStreamContent: string;
};

export class BodyField extends React.Component<Props, State> {
    handleChangeDebounced: QuillOnChange;

    stopTranscribeRef = React.createRef<() => void>();

    constructor(props: Props) {
        super(props);
        this.state = {
            value: props.input.value,
            valueSnapshot: '',
            latestStreamContent: '',
        };
        // @ts-expect-error
        this.stopTranscribeRef.current = this.stopTranscribeRef.current || (() => {});

        this.handleChangeDebounced = _.debounce((htmlValue, textValue) => {
            props.onBodyChange(props.input)(htmlValue, textValue);
        }, 200);
    }

    // If the form body is re-materialized (the user changed the primary recipient),
    // then we need to reset the form state.  We won't re-materialize if the body has
    // been changed in some way (dirtied).
    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.input.value !== this.state.value) {
            this.setState({value: nextProps.input.value});
        }
    }

    componentDidUpdate() {
        if (this.state.latestStreamContent.length) {
            this.props.onBodyChange(this.props.input)(
                `${this.state.value} ${this.state.latestStreamContent}`,
                ''
            );

            this.setState({latestStreamContent: ''});
        }
    }

    render() {
        const {
            fileAttachmentFieldName,
            input,
            isMultiSequence,
            meta,
            notificationBanner,
            isFocused,
            isActive,
        } = this.props;

        const tweakedInput = {
            value: this.state.value,
            onFocus: (e: any) => {
                input.onFocus(e);
            },
            onBlur: input.onBlur,
            // We actually mount and manage versions of this field at the same time,
            // (because a sequence can have 2+ emails in it), so we only want the active
            // one to call onChange. Not using meta.active (from redux-form) due to
            // some buggy behavior we've seen with calling initialize resetting the `active`
            // prop incorrectly (correctly?)
            onChange: isActive ? this.handleChange : () => {},
        } as const;

        return (
            <EmailEditor
                {...tweakedInput}
                liveTranscriptionComponent={
                    NutClientConfig.enableNotetaker && isFocused ? (
                        <TranscribeButtonContainer
                            onStartTranscribe={() => {
                                this.setState((prevState) => ({
                                    valueSnapshot: mutateQuillHtmlValueForTranscribing(
                                        prevState.value
                                    ),
                                }));
                            }}
                            handleTranscribe={(text, isFinal) => {
                                const newValue = `${this.state.valueSnapshot} ${text}`;
                                if (isFinal) {
                                    this.setState({
                                        value: newValue,
                                        valueSnapshot: newValue,
                                        latestStreamContent: '',
                                    });
                                } else {
                                    this.setState({value: newValue});
                                }
                            }}
                            stopTranscribe={(stopTranscribe) => {
                                // @ts-expect-error
                                this.stopTranscribeRef.current = stopTranscribe;
                            }}
                            location='email-composer'
                        />
                    ) : undefined
                }
                errorMessage={meta.submitFailed && !isMultiSequence ? meta.error : undefined}
                // @ts-expect-error
                toolbarLocation='bottom'
                notificationBanner={notificationBanner}
                fileAttachmentFieldName={fileAttachmentFieldName}
                customPlaceholderClickAction={PLACEHOLDER_ACTION_REPLACE}
                isLoading={this.props.isLoading}
                isBodyFocused={this.props.isBodyFocused}
                // same code exists in timeline-creator-form.js
                onKeyPress={() => {
                    if (this.stopTranscribeRef.current) {
                        this.stopTranscribeRef.current();
                    }
                }}
                // onKeyPress doesn't receive backspace events
                onKeyDown={(e) => {
                    if (this.stopTranscribeRef.current && doesKeyCancelTranscribe(e.key)) {
                        this.stopTranscribeRef.current();
                    }
                }}
                // Note that, due to how Quill works, it's possible that you
                // won't receive events for keys such as enter, backspace or
                // delete. If that's the case, try hooking onto onKeyUp instead.
                // https://www.npmjs.com/package/react-quill#usage
                onKeyUp={(e) => {
                    if (this.stopTranscribeRef.current && doesKeyCancelTranscribe(e.key)) {
                        this.stopTranscribeRef.current();
                    }
                }}
                insertLinkComponent={InsertQuoteLinkButton}
                suggestedReplyToInsert={this.props.suggestedReplyToInsert}
            />
        );
    }

    handleChange: QuillOnChange = (htmlValue, textValue) => {
        this.setState({value: htmlValue});
        this.handleChangeDebounced(htmlValue, textValue);
    };
}
